import React from "react";
import "../styles/process-visualization.css";
import "../styles/shared-ui.css";
import {
  downloadUseCaseDiagram,
  getUseCaseDiagram,
  diagramErrorMessage,
} from "../services/useCaseDiagramService";
import {
  downloadActivityDiagram,
  getActivityDiagram,
} from "../services/activityDiagramService";

// Each diagram's layout is fixed by the backend. `pixelRatio` is how many
// image pixels make one CSS pixel at 100% zoom (the activity PNG is 2x).
const DIAGRAMS = {
  usecase: {
    label: "Use-Case Diagram",
    fetch: (pid) => getUseCaseDiagram(pid, "svg"),
    download: downloadUseCaseDiagram,
    pixelRatio: 1,
    downloads: [
      { format: "svg", label: "Download SVG", title: "Download as SVG (sharp at any zoom)" },
      { format: "png", label: "Download PNG", title: "Download as PNG (2x resolution)" },
    ],
    // These mirror the diagram's colours.
    legend: [
      { role: "R", name: "Responsible (left)", color: "#1F5FAD" },
      { role: "A", name: "Accountable (right)", color: "#B3261E" },
    ],
    note: "Consulted (C) and Informed (I) roles and gateways are not drawn.",
  },
  activity: {
    label: "Activity Diagram",
    fetch: (pid) => getActivityDiagram(pid, "png"),
    download: downloadActivityDiagram,
    pixelRatio: 2,
    downloads: [
      { format: "report", label: "Download Report", title: "Download the Markdown report" },
      { format: "svg", label: "Download SVG", title: "Download as SVG (sharp at any zoom)" },
      { format: "png", label: "Download PNG", title: "Download as PNG (2x resolution)" },
    ],
    legend: [],
    note:
      "One swimlane per Responsible (R) job; tasks with no R job go in " +
      '"Unassigned". Gateways are drawn as fork/join (parallel) or ' +
      "decision/merge (exclusive, inclusive). Consulted (C) and Informed (I) " +
      "roles are not drawn.",
  },
};
const DIAGRAM_OPTIONS = [
  { value: "usecase", label: "Use Case" },
  { value: "activity", label: "Activity Diagram" },
];

const ZOOM_STEP = 0.15;
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 3;
const clampZoom = (z) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));

/**
 * Process UML tab of the Process Studio
 * Shows a UML diagram of the selected process, generated by the backend from
 * the process ID: the use-case diagram (tasks as use cases, Responsible jobs on
 * the left and Accountable jobs on the right) or the activity diagram (task
 * flow and gateways in swimlanes per Responsible job). The diagram loads as
 * soon as a process is selected.
 */
const ProcessUseCasePage = ({ processId: selectedId, processLabel: selectedLabel }) => {
  const [diagramType, setDiagramType] = React.useState("usecase");
  const diagram = DIAGRAMS[diagramType];
  // Read by the process-change effect without re-running it on type change.
  const diagramTypeRef = React.useRef(diagramType);
  diagramTypeRef.current = diagramType;

  const [imageUrl, setImageUrl] = React.useState("");
  const imageUrlRef = React.useRef("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [downloading, setDownloading] = React.useState(null);
  // Only the latest request may update the view when selections change quickly.
  const requestSeqRef = React.useRef(0);

  // Zoom is relative to the diagram's natural pixel size.
  const [zoom, setZoom] = React.useState(1);
  const [naturalWidth, setNaturalWidth] = React.useState(0);
  const viewportRef = React.useRef(null);
  // While true the diagram re-fits whenever its box changes width (window
  // resize, browser zoom, sidebar toggle). Zooming by hand turns it off so the
  // user's zoom is kept; "Fit" turns it back on.
  const autoFitRef = React.useRef(true);

  const revokeUrl = React.useCallback(() => {
    const current = imageUrlRef.current;
    if (current) {
      try {
        URL.revokeObjectURL(current);
      } catch (_) {}
    }
    imageUrlRef.current = "";
    setImageUrl("");
    setNaturalWidth(0);
  }, []);

  React.useEffect(
    () => () => {
      requestSeqRef.current += 1;
      revokeUrl();
    },
    [revokeUrl],
  );

  const loadDiagram = React.useCallback(
    async (pid, type) => {
      if (!pid) return;
      const seq = ++requestSeqRef.current;
      setLoading(true);
      setError("");
      revokeUrl();
      try {
        const blob = await DIAGRAMS[type].fetch(pid);
        if (seq !== requestSeqRef.current) return;
        const url = URL.createObjectURL(blob);
        imageUrlRef.current = url;
        setImageUrl(url);
      } catch (e) {
        if (seq !== requestSeqRef.current) return;
        console.error(`Failed to load ${type} diagram:`, e);
        setError(diagramErrorMessage(e));
      } finally {
        if (seq === requestSeqRef.current) setLoading(false);
      }
    },
    [revokeUrl],
  );

  // A new process (or none) replaces the diagram on screen.
  React.useEffect(() => {
    if (selectedId) {
      loadDiagram(selectedId, diagramTypeRef.current);
      return;
    }
    requestSeqRef.current += 1;
    setError("");
    setLoading(false);
    revokeUrl();
  }, [selectedId, loadDiagram, revokeUrl]);

  const onDiagramTypeChange = (type) => {
    setDiagramType(type);
    if (selectedId) loadDiagram(selectedId, type);
  };

  const fitZoom = React.useCallback((width) => {
    const viewport = viewportRef.current;
    if (!width || !viewport) return 1;
    const available = viewport.clientWidth - 32; // viewport padding
    // Shrink wide diagrams to fit; never blow small ones up past 100%.
    return clampZoom(Math.min(1, available / width));
  }, []);

  const handleImageLoad = (e) => {
    // Width in CSS pixels at 100% zoom.
    const width = (e.currentTarget.naturalWidth || 0) / diagram.pixelRatio;
    autoFitRef.current = true;
    setNaturalWidth(width);
    setZoom(fitZoom(width));
  };

  React.useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || !naturalWidth || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    // Only width matters for the fit; the box's height is fixed by the page.
    let lastWidth = viewport.clientWidth;
    let frame = 0;
    const ro = new ResizeObserver(() => {
      const width = viewport.clientWidth;
      if (width === lastWidth) return;
      lastWidth = width;
      if (!autoFitRef.current) return;
      // Next frame, so resizing the image never re-enters this observer's
      // own layout pass.
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setZoom(fitZoom(naturalWidth)));
    });
    ro.observe(viewport);
    return () => {
      ro.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [naturalWidth, fitZoom]);

  const zoomBy = (step) => {
    autoFitRef.current = false;
    setZoom((z) => clampZoom(z + step));
  };

  const fitToWidth = () => {
    autoFitRef.current = true;
    setZoom(fitZoom(naturalWidth));
  };

  const handleDownload = async (format) => {
    if (!selectedId) return;
    setDownloading(format);
    try {
      await diagram.download(selectedId, format);
    } catch (e) {
      console.error(`Failed to download ${diagramType} diagram (${format}):`, e);
      setError(diagramErrorMessage(e));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="ps-tab-body">
      <div className="role-card">
        <div className="section-title">Diagram</div>
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="uml-diagram-type">UML Diagram</label>
            <select
              id="uml-diagram-type"
              value={diagramType}
              onChange={(e) => onDiagramTypeChange(e.target.value)}
            >
              {DIAGRAM_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ alignSelf: "end" }}>
            <button
              className="primary-btn"
              onClick={() => loadDiagram(selectedId, diagramType)}
              disabled={!selectedId || loading}
              title="Fetch the diagram again, e.g. after editing the process"
            >
              {loading ? "Loading…" : "Reload Diagram"}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            marginTop: 12,
            padding: "12px 16px",
            borderRadius: 8,
            background: "var(--red-tint-100)",
            color: "var(--red-ink-800)",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          ⚠ {error}
        </div>
      )}

      {loading && !imageUrl && (
        <div className="role-card" style={{ marginTop: 12, color: "var(--gray-500)" }}>
          Loading diagram…
        </div>
      )}

      {imageUrl && (
        <div className="role-card" style={{ marginTop: 12 }}>
          <div
            className="section-title"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <span>
              {diagram.label}
              {loading && (
                <span style={{ marginLeft: 8, color: "var(--gray-500)" }}>
                  (Loading...)
                </span>
              )}
            </span>
            <span
              style={{
                display: "flex",
                gap: 16,
                fontSize: 13,
                fontWeight: 400,
                color: "var(--gray-600)",
                flexWrap: "wrap",
              }}
            >
              {diagram.legend.map((item) => (
                <span
                  key={item.role}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 18,
                      height: 3,
                      borderRadius: 2,
                      background: item.color,
                    }}
                  />
                  <strong style={{ color: item.color }}>{item.role}</strong>
                  {item.name}
                </span>
              ))}
            </span>
            <span className="roles-toolbar">
              {diagram.downloads.map((d, i) => (
                <button
                  key={d.format}
                  className={
                    i === diagram.downloads.length - 1 ?
                      "primary-btn"
                    : "secondary-btn"
                  }
                  onClick={() => handleDownload(d.format)}
                  disabled={!!downloading}
                  style={{ fontWeight: 600 }}
                  title={d.title}
                >
                  {downloading === d.format ? "Downloading…" : d.label}
                </button>
              ))}
            </span>
          </div>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--gray-500)" }}>
            {diagram.note}
          </p>

          <div className="po-slider">
            {/* Large processes grow tall, so the diagram scrolls in place.
                The low minimum keeps the box and the zoom bar under it on
                screen together on short laptop screens. A stable scrollbar
                gutter keeps the fit width from flipping as the vertical
                scrollbar comes and goes. */}
            <div
              ref={viewportRef}
              style={{
                height: "calc(100vh - 360px)",
                minHeight: 320,
                overflow: "auto",
                scrollbarGutter: "stable",
                background: "var(--color-surface)",
                borderRadius: 8,
                padding: 16,
              }}
            >
              <img
                src={imageUrl}
                alt={`${diagram.label} for ${selectedLabel || "process"}`}
                onLoad={handleImageLoad}
                style={{
                  display: "block",
                  margin: "0 auto",
                  maxWidth: "none",
                  height: "auto",
                  width: naturalWidth ? naturalWidth * zoom : "100%",
                }}
              />
            </div>
            <div className="po-zoom-controls">
              <button
                type="button"
                onClick={() => zoomBy(-ZOOM_STEP)}
                title="Zoom Out"
                aria-label="Zoom Out"
              >
                −
              </button>
              <span className="po-zoom-level">{Math.round(zoom * 100)}%</span>
              <button
                type="button"
                onClick={() => zoomBy(ZOOM_STEP)}
                title="Zoom In"
                aria-label="Zoom In"
              >
                +
              </button>
              <button
                type="button"
                onClick={fitToWidth}
                title="Reset"
                aria-label="Fit diagram to width"
              >
                Fit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessUseCasePage;
